import { defineComponent, h, withDirectives, resolveDirective } from 'vue';
import { QInput, QCard, QCardSection } from 'quasar';
import BaseComponent from './baseFormMixin.jsx';
import variableBindingMixin from './variableBindingMixin.jsx';
import {
    renderVariableAssistIcon,
    renderVariablePill,
} from './variableBindingUi.jsx';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';

import { appSettings } from '@/Shared/appSettings.js';

import TranslationContainer from './../TranslationContainer.jsx';
import VerticalStack from '../VerticalStack.jsx';

/**
 * Text input + header Controls (e.g. FilePlayer).
 * Variable assist lives in the header toolbar (not QInput append), same as textareaWithHeaderBar.
 */
export default defineComponent({
    name: 'sform-inputwithheaderbar',
    mixins: [BaseComponent, variableBindingMixin],
    components: {
        TranslationContainer,
        VerticalStack,
        QInput,
        QCard,
        QCardSection,
    },
    data: () => ({
    }),
    props: {
    },
    created() {
    },
    mounted() {
        this.loadComplete();
    },
    computed: {
        showHeaderVariableAssist() {
            return this.variableAssistAllowed && !this.isVariableBindingMode;
        },
    },
    methods: {
        renderHeaderVariableAssist() {
            if (!this.showHeaderVariableAssist)
                return null;

            const emphasize = this._vbHovered || this._vbFocused || this._vbPickerOpen;
            if (!emphasize)
                return null;

            return (
                <div
                    class="c-variable-binding-header-assist"
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        flex: '0 0 auto',
                        padding: '0 4px',
                    }}
                >
                    {renderVariableAssistIcon({
                        visible: true,
                        pickerOpen: this._vbPickerOpen,
                        onOpenPicker: () => this.openVariablePicker(),
                        menu: this.renderVariablePicker(),
                    })}
                </div>
            );
        },
        renderBoundBody() {
            const name = this.vbBoundName;
            if (!name)
                return null;

            return renderVariablePill({
                name,
                label: this.hasInputLabel ? (this.inputLabel || this.labelText) : undefined,
                onClear: () => this.exitVariableBindingMode(),
                onChange: () => this.openVariablePicker(),
                menu: this.renderVariablePicker(),
            });
        },
        renderLiteralInput() {
            let slots = {
                message: ({ message }) => {
                    return <TranslationContainer context={this} value={message}></TranslationContainer>;
                },
            };
            if (this.hasInputLabel)
                slots.label = () => <TranslationContainer context={this} value={this.labelText}></TranslationContainer>;
            if (this.hintText && appSettings.DebugTranslationPrefixSetting) {
                slots.hint = () => <TranslationContainer context={this} value={this.hintText}></TranslationContainer>;
            }

            let input = (
                <QInput
                    type="text"
                    class="caption q-pa-none q-ma-none sform-input-white"
                    outlined
                    dense
                    labelSlot={this.hasInputLabel}
                    ref={this.autofocus ? this.autofocus_id : undefined}
                    style={{ width: '100%' }}
                    modelValue={this.itemvalue}
                    rules={this.rules} {...this.compilerErrorFieldProps}
                    onUpdate:modelValue={(value) => { this.sync(value); }}
                    onFocus={() => { this._vbFocused = true; this.onFocus(); }}
                    onBlur={() => { this._vbFocused = false; this.onBlur(); }}
                    hint={this.inputHint}
                    hide-hint
                    persistent-hint={appSettings.DebugTranslationPrefixSetting}
                    bottom-slots={!!this.hintText || !!(this.rules && this.rules.length > 0)}
                >
                    {slots}
                </QInput>
            );

            if (this.appearance)
                utils.applyVue2ToVue3Props(input, this.appearance);

            if (this.directives) {
                var applyDirectives = this.directives.map(d => {
                    const dirDef = d.directive || resolveDirective(d.name);
                    return [dirDef, d.value, d.arg, d.modifiers];
                });
                input = withDirectives(input, applyDirectives);
            }

            return input;
        },
    },
    render() {
        const headerAssist = this.renderHeaderVariableAssist();
        const body = this.isVariableBindingMode
            ? this.renderBoundBody()
            : this.renderLiteralInput();

        return (
            <QCard
                flat
                bordered
                class="q-mb-sm q-pb-none c-InputWithHeaderBar"
                onMouseenter={this.onVariableAssistMouseEnter}
                onMouseleave={this.onVariableAssistMouseLeave}
            >
                <QCardSection class="q-pa-none q-pl-xs q-pr-xs">
                    <div
                        class="c-InputWithHeaderBar-toolbar"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            minHeight: '32px',
                            width: '100%',
                        }}
                    >
                        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                            <VerticalStack
                                type="HorizontalLayout"
                                name={this.name}
                                root={this.root}
                                parentType="InputWithHeaderBar"
                                controlData={this.element.formatData}
                                controlName={this.Name}
                                scopeitems={this.scopeitems}
                                controlscope={this.controlscope}
                            >
                            </VerticalStack>
                        </div>
                        {headerAssist}
                    </div>
                </QCardSection>
                <QCardSection class="q-pb-none">
                    {body}
                </QCardSection>
            </QCard>
        );
    }
});
